import { useState } from 'react';
import { Button, Flex } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag } from '@fortawesome/pro-regular-svg-icons';

import { isRemovableTag } from 'shared-components';
import { ITag, TagType } from 'types';

import { getAllTags } from '../../../../utilities/tags/getAllTags';
import { UserRole } from '../../../../store/auth/types';
import { hasPerm, P } from '../../../../utilities/roles/permissions';
import { TagComponent } from '../../../molecules/Tags';
import AlertDialog from '../../../pages/modals/AlertDialog';

import AddTagModal from './AddTagModal';
import { useTranslation } from "react-i18next";

interface IProps {
  role?: UserRole;
  tags: ITag[];
  allTags: ITag[];
  allProgrammableTags: ITag[];
  isPathway: boolean;
  studentNo: string;
  university: string;
  intakeId: string;
  offerId: string;
  disableEdit?: boolean;
  unpinTagsFromStudents: ({
    tags,
    studentNumbers,
  }: {
    tags: { id: string; isProgrammable: boolean }[];
    studentNumbers: string[];
  }) => void;
  pinTagsToStudents: ({
    tags,
    studentNumbers,
  }: {
    tags: { id: string; isProgrammable: boolean }[];
    studentNumbers: string[];
  }) => void;
}

const StudentHeaderTags = (props: IProps) => {
    const { t } = useTranslation('common');
  const {
    tags,
    isPathway,
    role,
    unpinTagsFromStudents,
    pinTagsToStudents,
    university,
    intakeId,
    offerId,
    studentNo,
    allTags,
    allProgrammableTags,
    disableEdit = false,
  } = props;
  const allStudentTags = getAllTags({
    tags,
    isPathway,
  });

  const canDeleteTag = role && hasPerm(role, P.TAGS_DELETE);

  const [isTagDeleteConfirmationModal, setTagDeleteConfirmationModal] = useState(false);
  const [tagDeleteModalData, setTagDeleteModalData] = useState({
    clickAction: () => {},
    title: '',
    buttonName: '',
  });

  const [isAddTagModal, setIsAddTagModal] = useState(false);
  const onAddTag = () => {
    setIsAddTagModal(true);
  };

  return (
    <Flex flexWrap={'wrap'} gap={{ base: 1, xl: 2 }}>
      <AlertDialog
        isOpen={isTagDeleteConfirmationModal}
        modalTitle={`Remove tag`}
        descriptionText={`Are you sure you want to remove this tag?`}
        confirmationButtonText={`Remove`}
        id={'remove-tag'}
        onClickSuccess={() => tagDeleteModalData.clickAction()}
        onClose={() => setTagDeleteConfirmationModal(false)}
      />

      {isAddTagModal && (
        <AddTagModal
          studentNo={studentNo}
          university={university}
          intakeId={intakeId}
          offerId={offerId}
          onClose={() => setIsAddTagModal(false)}
          tags={allTags}
          programmableTags={allProgrammableTags}
          pinTagsToStudents={pinTagsToStudents}
        />
      )}

      {allStudentTags.map((tag: ITag, idx: number) => {
        return (
          <TagComponent
            key={idx}
            tag={tag}
            canDelete={!disableEdit && canDeleteTag && isRemovableTag(tag)}
            onClick={() => {
              setTagDeleteModalData({
                title: 'Remove Tag From Student?',
                buttonName: 'Confirm',
                clickAction: () => {
                  unpinTagsFromStudents({
                    tags: [{ id: tag.id, isProgrammable: tag.type === TagType.PROGRAMMABLE }],
                    studentNumbers: [`${university}#${studentNo}#${intakeId}#${offerId}`],
                  });
                  setTagDeleteConfirmationModal(false);
                },
              });
              setTagDeleteConfirmationModal(true);
            }}
            tagProps={{ size: 'sm' }}
          />
        );
      })}

      {!disableEdit && (
        <Button
          onClick={onAddTag}
          data-selector={'add-tag'}
          variant={'ghost'}
          size={'sm'}
          height={6}
          px={2}
          borderRadius={'base'}
          leftIcon={<FontAwesomeIcon icon={faTag} className="utility-icon" />}
        >
          {t('common:addTag')}
                          </Button>
      )}
    </Flex>
  );
};

export default StudentHeaderTags;
